import { Link, NavLink, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Settings, ChevronRight, Building2, Search, Clock } from 'lucide-react'
import { Home } from 'lucide-react'
import { getAreaTree, areaKeys } from '@/lib/api/areas'
import { useUser } from '@/contexts/UserContext'
import type { AreaNode } from '@/lib/types/ged'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip'

export function AreaSidebar({ onOpenSearch }: { onOpenSearch: () => void }) {
  const { user } = useUser()
  const { data: tree = [], isLoading } = useQuery({
    queryKey: areaKeys.tree(),
    queryFn: getAreaTree,
  })

  const initials = user?.email
    ? user.email.slice(0, 2).toUpperCase()
    : '??'

  // Mostra "Área administrativa" se o user tem qualquer claim que não seja só "OwnProfile.*"
  const hasAdminAccess = user?.permissions.some((p) => !p.startsWith('OwnProfile.')) ?? false

  return (
    <aside
      style={{
        width: 260,
        flexShrink: 0,
        background: 'var(--ev-bg-elevated)',
        borderRight: '1px solid var(--ev-border)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Search shortcut */}
      <div style={{ padding: '14px 12px 8px' }}>
        <button
          onClick={onOpenSearch}
          style={{
            width: '100%',
            height: 34,
            padding: '0 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'var(--ev-bg-alt)',
            border: '1px solid var(--ev-border)',
            borderRadius: 6,
            color: 'var(--ev-fg-muted)',
            fontSize: 'var(--fs-small)',
            cursor: 'pointer',
          }}
        >
          <Search size={14} />
          <span>Buscar documentos…</span>
          <span
            style={{
              marginLeft: 'auto',
              fontFamily: 'ui-monospace, monospace',
              fontSize: 'var(--fs-micro)',
              padding: '2px 5px',
              border: '1px solid var(--ev-border)',
              borderRadius: 3,
            }}
          >
            ⌘K
          </span>
        </button>
      </div>

      {/* Static nav */}
      <nav style={{ padding: '4px 8px', display: 'flex', flexDirection: 'column', gap: 1 }}>
        <SideNavItem to="/" icon={<Home size={15} />} label="Índice" end />
        <SideNavItem to="/recents" icon={<Clock size={15} />} label="Recentes" />
      </nav>

      {/* Areas section label */}
      <div
        style={{
          padding: '12px 18px 6px',
          fontSize: 'var(--fs-micro)',
          fontWeight: 600,
          color: 'var(--ev-fg-faint)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        Áreas
      </div>

      {/* Top-level areas only — a árvore completa aparece dentro do Índice/páginas de cards */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 8px' }}>
        {isLoading && (
          <div style={{ padding: '8px 10px', fontSize: 'var(--fs-small)', color: 'var(--ev-fg-muted)' }}>
            Carregando…
          </div>
        )}
        <TooltipProvider delayDuration={300}>
          {tree.map((node) => (
            <TopLevelAreaItem key={node.id} node={node} />
          ))}
        </TooltipProvider>
      </div>

      {/* Admin link — só aparece para users com alguma claim de admin */}
      {hasAdminAccess && (
        <Link
          to="/admin"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            margin: '0 12px 8px',
            padding: '8px 10px',
            borderTop: '1px solid var(--ev-border)',
            borderRadius: '0 0 5px 5px',
            background: 'var(--ev-bg-alt)',
            color: 'var(--ev-accent)',
            fontSize: 'var(--fs-small)',
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Settings size={13} />
            Área administrativa
          </span>
          <ChevronRight size={11} />
        </Link>
      )}

      {/* User footer */}
      <div
        style={{
          borderTop: '1px solid var(--ev-border)',
          padding: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'var(--evertec-blue-500)',
            color: 'white',
            display: 'grid',
            placeItems: 'center',
            fontWeight: 600,
            fontSize: 'var(--fs-small)',
            flexShrink: 0,
          }}
        >
          {initials}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 'var(--fs-small)',
              fontWeight: 600,
              color: 'var(--ev-fg-strong)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {user?.email ?? ''}
          </div>
        </div>
        <Link
          to="/change-password"
          style={{
            background: 'none',
            border: 0,
            color: 'var(--ev-fg-muted)',
            cursor: 'pointer',
            padding: 4,
            textDecoration: 'none',
            display: 'flex',
          }}
        >
          <Settings size={14} />
        </Link>
      </div>
    </aside>
  )
}

/* ---- Internal helpers ---- */

function SideNavItem({
  to,
  icon,
  label,
  end,
}: {
  to: string
  icon: React.ReactNode
  label: string
  end?: boolean
}) {
  return (
    <NavLink
      to={to}
      end={end}
      style={({ isActive }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '7px 10px',
        borderRadius: 5,
        background: isActive ? 'var(--ev-bg-active)' : 'transparent',
        color: isActive ? 'var(--ev-accent)' : 'var(--ev-fg)',
        fontSize: 'var(--fs-small)',
        fontWeight: isActive ? 600 : 500,
        cursor: 'pointer',
        textDecoration: 'none',
      })}
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  )
}

function TopLevelAreaItem({ node }: { node: AreaNode }) {
  const { slug: activeSlug } = useParams<{ slug?: string }>()
  const isActive = node.slug === activeSlug
  const navigate = useNavigate()

  return (
    <div
      onClick={() => navigate(`/areas/${node.slug}`)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 8px',
        borderRadius: 4,
        background: isActive ? 'var(--ev-bg-active)' : 'transparent',
        color: isActive ? 'var(--ev-accent)' : 'var(--ev-fg)',
        fontSize: 'var(--fs-small)',
        fontWeight: isActive ? 600 : 500,
        cursor: 'pointer',
        minHeight: 28,
      }}
    >
      <Building2
        size={14}
        style={{ color: isActive ? 'var(--ev-accent)' : 'var(--ev-fg-muted)', flexShrink: 0 }}
      />
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-tooltip-name={node.name}
            style={{
              flex: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {node.name}
          </span>
        </TooltipTrigger>
        <TooltipContent side="right">{node.name}</TooltipContent>
      </Tooltip>
    </div>
  )
}
